import { useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { CloudUpload, InfoOutlined, InsertDriveFile } from "@mui/icons-material";
import AppDialog from "../common/AppDialog";
import {
  ClientAutocompleteListbox,
  clientAutocompletePaperProps,
} from "../userManagement/clientAutocomplete";
import { useAppDispatch } from "features/store";
import { pushNotification } from "features/notificationSlice";
import {
  useFinalizeSecureUploadMutation,
  useGetSecureClientsQuery,
  useGetSecureRecipientsQuery,
  usePresignSecureUploadMutation,
} from "features/secureUploads/secureUploads";

interface Props {
  open: boolean;
  onClose: () => void;
  isInternalUploader: boolean;
}

const formatBytes = (bytes: number): string => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

export default function SecureFileUploadModal({
  open,
  onClose,
  isInternalUploader,
}: Props) {
  const dispatch = useAppDispatch();

  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [clientId, setClientId] = useState<number | "">("");
  const [recipientId, setRecipientId] = useState<number | "">("");
  const [message, setMessage] = useState("");
  const [silent, setSilent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: clients = [] } = useGetSecureClientsQuery(undefined, {
    skip: !open || !isInternalUploader,
  });

  const recipientQueryArg = isInternalUploader
    ? { client_id: clientId || undefined }
    : {};
  const { data: recipients = [], isFetching: recipientsLoading } =
    useGetSecureRecipientsQuery(recipientQueryArg, {
      skip: !open || (isInternalUploader && !clientId),
    });

  const [presign] = usePresignSecureUploadMutation();
  const [finalize] = useFinalizeSecureUploadMutation();

  const selectedRecipient = useMemo(
    () => recipients.find((r) => r.id === recipientId),
    [recipients, recipientId],
  );

  const reset = () => {
    setFile(null);
    setDragOver(false);
    setClientId("");
    setRecipientId("");
    setMessage("");
    setSilent(false);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleFiles = (files: FileList | null) => {
    if (files && files.length > 0) {
      setFile(files[0]);
    }
  };

  const canSubmit =
    !!file && !!recipientId && (!isInternalUploader || !!clientId) && !submitting;

  const handleSubmit = async () => {
    if (!file || !recipientId || submitting) return;
    setSubmitting(true);
    try {
      const presigned = await presign({
        file_name: file.name,
        content_type: file.type || "application/octet-stream",
      }).unwrap();

      const putRes = await fetch(presigned.upload_url, {
        method: "PUT",
        headers: presigned.headers,
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`);
      }

      const result = await finalize({
        s3_key: presigned.s3_key,
        recipient_id: recipientId as number,
        client_id: isInternalUploader ? (clientId as number) : undefined,
        file_name: presigned.file_name,
        content_type: file.type || "application/octet-stream",
        message: message.trim(),
        is_silent: isInternalUploader ? silent : undefined,
      }).unwrap();

      dispatch(
        pushNotification({
          message: result.is_silent
            ? "File shared without notification."
            : result.email_sent
              ? "File shared. An email notification was sent."
              : "File shared successfully.",
          severity: "success",
          autoHideMs: 6000,
        }),
      );
      reset();
      onClose();
    } catch (err) {
      dispatch(
        pushNotification({
          message:
            (err as { data?: { error?: string } })?.data?.error ||
            "Failed to upload the file. Please try again.",
          severity: "error",
        }),
      );
      setSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Upload New File"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {submitting ? "Uploading…" : "Upload"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: dragOver ? "primary.main" : "divider",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: dragOver ? "action.hover" : "background.default",
            transition: "border-color 0.15s ease, background-color 0.15s ease",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(e) => handleFiles(e.target.files)}
          />
          {file ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <InsertDriveFile color="primary" />
              <Box sx={{ textAlign: "left", minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 600 }}>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatBytes(file.size)} — click to replace
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <CloudUpload sx={{ fontSize: 36, color: "text.secondary" }} />
              <Typography sx={{ mt: 1 }}>
                Drag &amp; drop a file here, or click to browse
              </Typography>
            </>
          )}
        </Box>

        {isInternalUploader && (
          <Autocomplete
            size="small"
            fullWidth
            options={clients}
            getOptionLabel={(c) => c.company_name}
            getOptionKey={(c) => c.id}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={clients.find((c) => c.id === clientId) ?? null}
            onChange={(_, v) => {
              setClientId(v?.id ?? "");
              setRecipientId("");
            }}
            componentsProps={clientAutocompletePaperProps}
            ListboxComponent={ClientAutocompleteListbox}
            renderOption={(props, c) => (
              <li {...props} key={c.id}>
                <Box sx={{ width: "100%", py: 0.4 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {c.company_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", lineHeight: 1.25 }}
                  >
                    {c.company_code}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client"
                placeholder="Search clients…"
              />
            )}
          />
        )}

        <Autocomplete
          size="small"
          fullWidth
          disabled={isInternalUploader && !clientId}
          options={recipients}
          loading={recipientsLoading}
          getOptionLabel={(r) => r.name}
          getOptionKey={(r) => r.id}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={recipients.find((r) => r.id === recipientId) ?? null}
          onChange={(_, v) => setRecipientId(v?.id ?? "")}
          componentsProps={clientAutocompletePaperProps}
          ListboxComponent={ClientAutocompleteListbox}
          noOptionsText={
            isInternalUploader
              ? clientId
                ? "No client users at this client have Secure Exchange access"
                : "Select a client first"
              : "No recipients available"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={isInternalUploader ? "Send to (client user)" : "Send to"}
              placeholder="Search…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {recipientsLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {isInternalUploader && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              bgcolor: "action.hover",
              borderRadius: 1,
              p: 1.25,
            }}
          >
            <InfoOutlined sx={{ fontSize: 18, color: "info.main", mt: "1px" }} />
            <Typography variant="caption" color="text.secondary">
              Only client users with Secure Exchange access are listed —
              anyone without it can&apos;t open this section, so they won&apos;t
              receive the file or its email notification. Enable the role in User
              Management if a recipient is missing.
            </Typography>
          </Box>
        )}

        {isInternalUploader && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={silent}
                  onChange={(e) => setSilent(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  Send without notification
                </Typography>
              }
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", ml: 3.5, mt: -0.5 }}
            >
              The recipient won&apos;t receive an email or in-app notification —
              the file will simply appear in their Secure Exchange.
            </Typography>
          </Box>
        )}

        {selectedRecipient && (
          <Typography variant="caption" color="text.secondary">
            {silent
              ? "No notifications will be sent — the file will just appear in the recipient's Secure Exchange."
              : selectedRecipient.has_email
                ? `An email notification will be sent to ${selectedRecipient.email}.`
                : "This user has no email — they'll only get an in-app notification."}
          </Typography>
        )}

        <TextField
          label="Message to Recipient"
          placeholder="Add a message to include with the document"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          multiline
          minRows={2}
          fullWidth
          size="small"
        />
      </Box>
    </AppDialog>
  );
}
